import {
  Alert,
  Button,
  ChoiceChips,
  Col,
  FeedItem,
  type IconName,
  Input,
  ListEmpty,
  PageHeader,
  SectionLabel,
  SkeletonRows,
  Stack,
} from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { EVERY_KIND, isOfKind, matchesTheSearch, type OfAKind, theDayItHappened } from "../feed/of-a-kind.ts";
import { type Happening, howToReadIt, whereItHappened, whoDidIt } from "../feed/what-happened.ts";
import { say } from "../localization/say.ts";
import { read } from "../net/rally.ts";
import { goTo } from "../router.ts";
import { reloadNow, useUpdateReady } from "../update.ts";

const A_PAGE = 60;

const GLYPH: Record<OfAKind, IconName> = {
  all: "activity",
  releases: "rocket",
  messages: "message-circle",
  mentions: "at-sign",
  files: "files",
  calls: "phone",
  apps: "box",
  agents: "bot",
  people: "users",
};

function byDay(shown: readonly Happening[]): Array<[string, Happening[]]> {
  const now = new Date();
  const days = new Map<string, Happening[]>();
  for (const one of shown) {
    const day = theDayItHappened(one.at, now);
    const held = days.get(day);
    if (held) held.push(one);
    else days.set(day, [one]);
  }
  return [...days.entries()];
}

function dayCalled(day: string): string {
  if (day === "today") return say("activity.today");
  if (day === "yesterday") return say("activity.yesterday");
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 0, month ?? 0, date ?? 1).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
  });
}

function whereItLeadsTo(happening: Happening): string | null {
  if (happening.conversation) {
    const message = typeof happening.payload.message === "string" ? happening.payload.message : null;
    return `/c/${happening.conversation.id}${message ? `?message=${message}` : ""}`;
  }
  if (happening.project) return `/p/${happening.project.id}`;
  return null;
}

export function Activity({ me }: { me: string }) {
  const [happenings, setHappenings] = useState<Happening[] | null>(null);
  const [kind, setKind] = useState<OfAKind>("all");
  const [looking, setLooking] = useState("");
  const [older, setOlder] = useState(false);
  const updated = useUpdateReady();

  const load = useCallback(async (before: string | null) => {
    const asks: Record<string, string> = { scope: "server", limit: String(A_PAGE) };
    if (before) asks.before = before;
    return read<Happening[]>("feed.read", asks);
  }, []);

  useEffect(() => {
    void load(null)
      .then(setHappenings)
      .catch(() => setHappenings([]));
  }, [load]);

  const more = async () => {
    const last = happenings?.[happenings.length - 1];
    if (!last || older) return;
    setOlder(true);
    const next = await load(last.seq).catch(() => []);
    setHappenings((was) => [...(was ?? []), ...next]);
    setOlder(false);
  };

  const shown = (happenings ?? []).filter(
    (one) => isOfKind(one, kind, me) && matchesTheSearch(one, howToReadIt(one).line, looking),
  );

  return (
    <Stack gap={4} testID="details-activity">
      <PageHeader title={say("activity.title")} {...(happenings === null ? {} : { count: shown.length })} />

      {updated ? (
        <Alert
          tone="info"
          icon="sparkles"
          title={say("new.updated")}
          {...({ testID: "activity-update" } as { testID: string })}
        >
          <Button variant="soft" size="sm" onPress={reloadNow} testID="activity-reload">
            {say("new.reload")}
          </Button>
        </Alert>
      ) : null}

      <Input
        icon="search"
        value={looking}
        onChangeText={setLooking}
        placeholder={say("activity.search")}
        accessibilityLabel={say("activity.search")}
        testID="activity-search"
      />

      <ChoiceChips
        size="sm"
        value={kind}
        onChange={setKind}
        testID="activity-kinds"
        options={EVERY_KIND.map((one) => ({
          value: one,
          label: say(`activity.kind.${one}` as "activity.kind.all"),
          icon: GLYPH[one],
          testID: `activity-kind-${one}`,
        }))}
      />

      {happenings === null ? (
        <SkeletonRows rows={6} face={36} />
      ) : shown.length === 0 ? (
        <ListEmpty
          fill
          icon={GLYPH[kind]}
          line={looking.trim() ? say("activity.noMatch")(looking.trim()) : say("activity.none")}
        />
      ) : (
        <Col gap="none" style={{ width: "100%", maxWidth: 960 }} testID="activity-list">
          {byDay(shown).map(([day, held]) => (
            <Col key={day} gap="none" style={{ width: "100%" }}>
              <SectionLabel title={dayCalled(day)} />
              {held.map((happening, index) => {
                const reading = howToReadIt(happening);
                const to = whereItLeadsTo(happening);
                return (
                  <FeedItem
                    key={happening.id}
                    who={whoDidIt(happening)}
                    face={happening.by.avatar ? `/o/${happening.by.avatar}` : undefined}
                    tone={reading.tone}
                    at={new Date(happening.at)}
                    line={reading.line}
                    where={whereItHappened(happening)}
                    last={index === held.length - 1}
                    {...(to ? { onPress: () => goTo(to) } : {})}
                  />
                );
              })}
            </Col>
          ))}
        </Col>
      )}

      {happenings !== null && happenings.length >= A_PAGE ? (
        <Col style={{ alignItems: "center" }}>
          <ChoiceChips
            size="sm"
            value={null}
            onChange={() => void more()}
            testID="activity-more"
            options={[{ value: "more", label: older ? say("activity.loading") : say("activity.more") }]}
          />
        </Col>
      ) : null}
    </Stack>
  );
}
